import { Button } from '@kinetik/ui-kit';
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { makeSelectActiveOrgResourceId } from 'containers/App/selectors';

import {
  Actions,
  ContentWrapper,
  ErrorDetails,
  ErrorPre,
  FallbackContainer,
  Heading,
  SubText,
} from './AppErrorBoundary.styles';

const selectActiveOrgResourceId = makeSelectActiveOrgResourceId();

/**
 * Fallback rendered by AppErrorBoundary once a render throw has unmounted the
 * app body. Both actions recover in place so the user never has to close the
 * tab (TS-9473): "Try again" clears the boundary so the subtree remounts with
 * fresh component state, and "Go to home" additionally navigates away from the
 * route that threw, for the case where the bad state lives in the URL.
 */
const AppErrorFallback = ({ error, errorInfo, onReset }) => {
  const history = useHistory();
  const activeOrgResourceId = useSelector(selectActiveOrgResourceId);

  const handleGoHome = () => {
    history.replace(activeOrgResourceId ? `/${activeOrgResourceId}` : '/');
    onReset();
  };

  return (
    <FallbackContainer>
      <ContentWrapper>
        <Heading>Something went wrong</Heading>
        <SubText>
          This page ran into an unexpected error. You can try again, or go back
          to home.
        </SubText>
        <Actions>
          <Button variant="primary-light" size="large" onClick={onReset}>
            Try again
          </Button>
          <Button variant="secondary" size="large" onClick={handleGoHome}>
            Go to home
          </Button>
        </Actions>
        {process.env.NODE_ENV === 'development' && error && (
          <ErrorDetails>
            <summary>Error Details (Development Only)</summary>
            <ErrorPre>
              {error.toString()}
              {errorInfo && errorInfo.componentStack}
            </ErrorPre>
          </ErrorDetails>
        )}
      </ContentWrapper>
    </FallbackContainer>
  );
};

AppErrorFallback.propTypes = {
  error: PropTypes.object,
  errorInfo: PropTypes.object,
  onReset: PropTypes.func.isRequired,
};

export default AppErrorFallback;
